// iter87 — App refresh helpers shared by:
//   • UpdateBanner ("New version available — Refresh Now")
//   • Profile dropdown ("Refresh App")
//   • Mobile pull-to-refresh
//
// Two distinct flavors:
//   softReload()     — keeps caches; just `window.location.reload()`. Use when a new SW is
//                      already activated and we only need the page to pick up the new bundle.
//   resetAndReload() — clears every service-worker cache + unregisters every SW + reloads.
//                      Use only when the user is stuck on a stale build that soft-reload
//                      can't recover.  PRESERVES the keys listed in SAFE_KEEP below.
//
// Both flavors are explicitly safe for field staff: they never touch the auth cookie
// (the browser owns it server-side) and never delete in-progress drafts.

const APP_VERSION_KEY = 'nexflow_app_version';

// Allowlist of localStorage prefixes that MUST survive a cache reset.
// Anything else under nexflow.* is considered disposable UI state.
// Drafts: completion wizards / invoices / typed notes save here as `nexflow.draft.<jobId>` etc.
const SAFE_KEEP_PREFIXES = [
  'nexflow.draft.',          // job completion drafts, invoice drafts, typed notes
  'nexflow.draft_',          // legacy underscore variants
  'nexflow.pinned.',         // sidebar pinned favorites (also persisted server-side, but kept locally for instant render)
  'nexflow.sidebar.scroll.', // sidebar scroll position
  'nexflow.pin_favorites',   // legacy single-key favorites snapshot
  'nexflow_app_version',     // our own version stamp
  'nexflow_sw_v',            // SW recovery flags
];

const SAFE_KEEP_SESSION_PREFIXES = [
  'nexflow.sidebar.scroll.',
];

function isSafeKey(key, prefixes) {
  return prefixes.some((p) => key.startsWith(p));
}

/** Snapshot localStorage + sessionStorage values worth preserving across a hard reset. */
function snapshotSafeStorage() {
  const safeLS = {};
  const safeSS = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && isSafeKey(k, SAFE_KEEP_PREFIXES)) safeLS[k] = window.localStorage.getItem(k);
    }
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && isSafeKey(k, SAFE_KEEP_SESSION_PREFIXES)) safeSS[k] = window.sessionStorage.getItem(k);
    }
  } catch { /* private mode / quota */ }
  return { safeLS, safeSS };
}

function restoreSafeStorage(snapshot) {
  try {
    Object.entries(snapshot.safeLS || {}).forEach(([k, v]) => window.localStorage.setItem(k, v));
    Object.entries(snapshot.safeSS || {}).forEach(([k, v]) => window.sessionStorage.setItem(k, v));
  } catch { /* */ }
}

/** Soft reload — bundle refresh only. Drafts, favorites, scroll, auth cookie all preserved
 *  by default because nothing is cleared. Use this when a new SW has already activated. */
export const softReload = () => {
  try { window.location.reload(); } catch { /* */ }
};

/** Hard reset — clears every SW cache + unregisters every SW + reloads.
 *  Auth cookie survives (server-set, not JS-readable). Drafts/favorites survive
 *  because we snapshot + restore the SAFE_KEEP keys around the reset. */
export const resetAndReload = async () => {
  const snapshot = snapshotSafeStorage();
  try {
    // 1) Drop everything from localStorage that's NOT in the safe list, so any corrupt
    // session-only UI state is gone. (Drafts/favorites/scroll already snapshotted.)
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && !isSafeKey(k, SAFE_KEEP_PREFIXES)) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch { /* */ }

  // 2) Drop all caches the SW manages.
  if ('caches' in window) {
    try {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    } catch { /* */ }
  }

  // 3) Unregister every service worker for this origin so the browser fetches a fresh one
  //    on the next load.
  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    } catch { /* */ }
  }

  // 4) Restore the safe-keep snapshot AFTER the wipe (any of them removed above? we put back).
  restoreSafeStorage(snapshot);

  // 5) Force-reload bypassing the HTTP cache where possible.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_r', Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};

/** Store the current app version (CACHE name reported by the SW) so we can compare on next load. */
export const recordAppVersion = (version) => {
  try { window.localStorage.setItem(APP_VERSION_KEY, version); } catch { /* */ }
};

export const getKnownAppVersion = () => {
  try { return window.localStorage.getItem(APP_VERSION_KEY); } catch { return null; }
};
